import { BarChart3 } from "lucide-react";

const pretty = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const SENIORITY_ORDER = ["intern", "entry", "mid", "senior", "lead_manager", "director_exec"];

function group(apps, key) {
  const m = {};
  apps.forEach((a) => {
    const v = a.labels?.[key];
    if (!v) return;
    m[v] = m[v] || { label: v, total: 0, interviews: 0 };
    m[v].total += 1;
    if (a.status === "interview" || a.status === "offer") m[v].interviews += 1;
  });
  return Object.values(m).map((g) => ({ ...g, rate: Math.round((g.interviews / g.total) * 100) }));
}

function Column({ title, rows, testid }) {
  const best = rows.reduce((b, r) => (r.interviews > 0 && (!b || r.rate > b.rate) ? r : b), null);
  return (
    <div data-testid={testid}>
      <div className="text-xs font-mono2 uppercase tracking-widest text-faint mb-3">{title}</div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.label} data-testid={`${testid}-row-${r.label}`}>
            <div className="flex items-center justify-between text-sm mb-1">
              <span className={best?.label === r.label ? "text-brand font-semibold" : "text-body"}>{pretty(r.label)}</span>
              <span className="font-mono2 text-xs text-body">{r.interviews}/{r.total} · {r.rate}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-subtle overflow-hidden">
              <div className="h-full rounded-full bg-brand transition-[width] duration-700" style={{ width: `${Math.max(r.rate, 3)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function FitInsights({ applications }) {
  const labelled = applications.filter((a) => a.labels && a.processing_status === "done");
  if (labelled.length < 2) return null;
  const industries = group(labelled, "industry").sort((a, b) => b.rate - a.rate || b.total - a.total).slice(0, 6);
  const seniority = group(labelled, "seniority").sort((a, b) => SENIORITY_ORDER.indexOf(a.label) - SENIORITY_ORDER.indexOf(b.label));
  const all = [...industries, ...seniority];
  const anyInterviews = all.some((r) => r.interviews > 0);
  const topInd = industries.find((r) => r.interviews > 0);
  const topSen = [...seniority].sort((a, b) => b.rate - a.rate).find((r) => r.interviews > 0);

  return (
    <div className="rounded-xl border border-line bg-surface p-5 mb-7 af-fade-up" data-testid="fit-insights">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="w-4 h-4 text-brand" />
        <span className="font-display font-bold">Where you get interviews</span>
      </div>
      <p className="text-sm text-body mb-5" data-testid="fit-insights-headline">
        {anyInterviews
          ? <>You land the most interviews in <span className="text-brand font-semibold">{pretty(topInd?.label)}</span>{topSen && <> at <span className="text-brand font-semibold">{pretty(topSen.label)}</span> level</>}. Lean into these.</>
          : "No interviews yet. Mark applications as Interview or Offer and we'll show where you're strongest."}
      </p>
      <div className="grid md:grid-cols-2 gap-6">
        <Column title="By industry" rows={industries} testid="fit-insights-industry" />
        <Column title="By seniority" rows={seniority} testid="fit-insights-seniority" />
      </div>
    </div>
  );
}
